import rawSeatMatrix from '../data/ugeac/seatMatrix2026.generated.json';
import rawCollegeWebsites from '../data/collegeWebsites.generated.json';
import { getBranchPriority } from './predictor.js';
import { getCollegePriority } from './collegePriority.js';

const COLLEGE_ALIASES = {
  'govt engg college munger': 'Govt. Engg.College Munger',
  'govt engg college of munger': 'Govt. Engg.College Munger',
  'govt engg college munger bihar': 'Govt. Engg.College Munger',
};

const normalizeKey = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/engg\./g, 'engineering')
    .replace(/engg/g, 'engineering')
    .replace(/govt\./g, 'government')
    .replace(/govt/g, 'government')
    .replace(/inst\./g, 'institute')
    .replace(/inst/g, 'institute')
    .replace(/tech\./g, 'technology')
    .replace(/tech/g, 'technology')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const getCanonicalCollegeName = (value = '') => {
  const key = normalizeKey(value);
  return COLLEGE_ALIASES[key] || value;
};

const normalizeSeatMatrixEntry = (entry) => ({
  ...entry,
  college: getCanonicalCollegeName(entry.college),
  collegePriority: getCollegePriority(getCanonicalCollegeName(entry.college)),
  branchPriority: getBranchPriority(entry.branch, entry.branchLabel),
});

export const seatMatrixData = rawSeatMatrix
  .map(normalizeSeatMatrixEntry)
  .sort(
    (first, second) =>
      first.collegePriority - second.collegePriority ||
      first.branchPriority - second.branchPriority ||
      first.branchLabel.localeCompare(second.branchLabel),
  );

const websiteEntries = rawCollegeWebsites.map((row) => ({
  ...row,
  college: getCanonicalCollegeName(row.college),
}));

export const collegeWebsiteMap = Object.fromEntries(
  websiteEntries.map((row) => [row.college, row.website]),
);

export const seatMatrixCollegeOptions = [...new Set(seatMatrixData.map((row) => row.college))];

export const seatTypeOptions = ['All', 'General', 'Female'];

const FEMALE_CATEGORY_LABELS = {
  UR: 'F_UR',
  SC: 'F_SC',
  ST: 'F_ST',
  EBC: 'F_EBC',
  BC: 'F_BC',
  EWS: 'F_EWS',
  RCG: 'RCG',
};

const GENERAL_CATEGORY_ORDER = ['All', 'UR', 'SC', 'ST', 'EBC', 'BC', 'RCG', 'EWS', 'DQ', 'SMQ', 'NRI'];
const FEMALE_CATEGORY_ORDER = ['All', 'UR', 'SC', 'ST', 'EBC', 'BC', 'RCG', 'EWS'];

const getCategoryKeyForSeatType = (seatType, category) => {
  if (category === 'All') {
    return null;
  }
  if (seatType === 'Female') {
    return FEMALE_CATEGORY_LABELS[category] || null;
  }
  return category;
};

export const getSeatCountForFilter = (counts = {}, seatType = 'All', category = 'All') => {
  if (!counts) {
    return 0;
  }

  if (seatType === 'All' && category === 'All') {
    return counts.Total ?? 0;
  }

  if (seatType === 'All') {
    const generalCount = counts[category] ?? 0;
    const femaleCount = counts[FEMALE_CATEGORY_LABELS[category]] ?? 0;
    return generalCount + femaleCount;
  }

  const key = getCategoryKeyForSeatType(seatType, category);
  return key ? counts[key] ?? 0 : 0;
};

export const getSeatMatrixCategoryOptions = (rows, seatType = 'All') => {
  const availableKeys = new Set();
  rows.forEach((row) => {
    Object.keys(row.counts || {}).forEach((key) => availableKeys.add(key));
  });

  const order = seatType === 'Female' ? FEMALE_CATEGORY_ORDER : GENERAL_CATEGORY_ORDER;
  const options = order.filter((category) => {
    if (category === 'All') return true;
    if (seatType === 'Female') {
      const femaleKey = FEMALE_CATEGORY_LABELS[category];
      return category === 'RCG' ? availableKeys.has('RCG') : availableKeys.has(femaleKey);
    }
    return availableKeys.has(category);
  });

  return options;
};

export const filterSeatMatrixRows = ({
  college = 'All',
  branch = 'All',
  seatType = 'All',
  category = 'All',
} = {}) =>
  seatMatrixData
    .filter((row) => college === 'All' || row.college === college)
    .filter((row) => branch === 'All' || row.rawBranch === branch || row.branch === branch)
    .map((row) => ({
      ...row,
      filteredSeatCount: getSeatCountForFilter(row.counts, seatType, category),
    }))
    .filter((row) => (category === 'All' ? true : row.filteredSeatCount > 0))
    .sort(
      (first, second) =>
        first.collegePriority - second.collegePriority ||
        first.branchPriority - second.branchPriority ||
        first.branchLabel.localeCompare(second.branchLabel),
    );
